// How long the dropdown stays open after the cursor leaves the trigger or the
// sub-nav. Bridges the gap between the two (they aren't nested) so moving the
// mouse across it doesn't flicker the panel shut. Re-entering either cancels it.
const DROPDOWN_CLOSE_DELAY = 150;

if (!customElements.get("site-header")) {
  customElements.define(
    "site-header",
    class SiteHeader extends HTMLElement {
      connectedCallback() {
        // ── Dropdown sub-navs (desktop, hover-driven) ──
        this.nav = this.querySelector(".site-header__nav");
        this.triggers = Array.from(this.querySelectorAll("[data-target]"));
        this.subnavs = Array.from(this.querySelectorAll("[data-subnav]"));
        this.current = null;
        this.closeTimer = null;

        this.handleEnter = this.handleEnter.bind(this);
        this.handleLeave = this.handleLeave.bind(this);
        this.handleFocusIn = this.handleFocusIn.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleNavKeydown = this.handleNavKeydown.bind(this);

        // Hover opens/keeps the panel; pointer covers both the trigger and its
        // sub-nav row so the cursor can travel between them without closing.
        [...this.triggers, ...this.subnavs].forEach((el) => {
          el.addEventListener("mouseenter", this.handleEnter);
          el.addEventListener("mouseleave", this.handleLeave);
        });

        // Keyboard parity: focus opens, tabbing out closes, Escape dismisses.
        this.nav?.addEventListener("focusin", this.handleFocusIn);
        this.nav?.addEventListener("focusout", this.handleFocusOut);
        this.nav?.addEventListener("keydown", this.handleNavKeydown);

        // ── Mobile drawer ──
        this.drawer = this.querySelector("[data-drawer]");
        this.openBtn = this.querySelector("[data-drawer-open]");
        this.closeEls = Array.from(this.querySelectorAll("[data-drawer-close]"));

        this.openDrawer = this.openDrawer.bind(this);
        this.closeDrawer = this.closeDrawer.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);

        this.openBtn?.addEventListener("click", this.openDrawer);
        this.closeEls.forEach((el) => el.addEventListener("click", this.closeDrawer));
      }

      disconnectedCallback() {
        [...this.triggers, ...this.subnavs].forEach((el) => {
          el.removeEventListener("mouseenter", this.handleEnter);
          el.removeEventListener("mouseleave", this.handleLeave);
        });
        this.nav?.removeEventListener("focusin", this.handleFocusIn);
        this.nav?.removeEventListener("focusout", this.handleFocusOut);
        this.nav?.removeEventListener("keydown", this.handleNavKeydown);
        this.cancelClose();
        this.openBtn?.removeEventListener("click", this.openDrawer);
        this.closeEls.forEach((el) => el.removeEventListener("click", this.closeDrawer));
        document.removeEventListener("keydown", this.handleKeydown);
        document.body.classList.remove("drawer-open");
      }

      // ── Dropdowns (hover + keyboard) ──
      // Entering a trigger or its sub-nav opens/keeps that group.
      handleEnter(event) {
        this.cancelClose();
        const el = event.currentTarget;
        this.open(el.dataset.target || el.dataset.subnav);
      }

      // Leaving either doesn't close immediately — DROPDOWN_CLOSE_DELAY gives the
      // cursor time to cross the gap into the other element (which cancels it).
      handleLeave() {
        this.scheduleClose();
      }

      handleFocusIn(event) {
        this.cancelClose();
        const trigger = event.target.closest("[data-target]");
        // Focus on a trigger opens its group; focus on a sub-link just keeps
        // the current group open (cancelClose above already handled that).
        if (trigger) this.open(trigger.dataset.target);
      }

      handleFocusOut(event) {
        // Only close once focus has actually left the whole nav.
        if (!this.nav.contains(event.relatedTarget)) this.open(null);
      }

      handleNavKeydown(event) {
        if (event.key === "Escape") this.open(null);
      }

      // Hover-intent close: wait DROPDOWN_CLOSE_DELAY so the cursor can cross the
      // gap into the other element (which calls cancelClose to keep it open).
      scheduleClose() {
        this.cancelClose();
        this.closeTimer = window.setTimeout(() => this.open(null), DROPDOWN_CLOSE_DELAY);
      }

      cancelClose() {
        if (this.closeTimer) {
          window.clearTimeout(this.closeTimer);
          this.closeTimer = null;
        }
      }

      open(target) {
        this.triggers.forEach((trigger) => {
          const isOpen = trigger.dataset.target === target;
          trigger.classList.toggle("is-open", isOpen);
          trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
        });
        this.subnavs.forEach((subnav) => {
          subnav.hidden = subnav.dataset.subnav !== target;
        });
        this.current = target;
      }

      // ── Drawer ──
      openDrawer() {
        if (!this.drawer || !this.drawer.hidden) return;
        this.drawer.hidden = false;
        void this.drawer.offsetWidth; // reflow so the slide-in transition runs
        this.drawer.classList.add("is-open");
        document.body.classList.add("drawer-open");
        this.openBtn?.setAttribute("aria-expanded", "true");
        document.addEventListener("keydown", this.handleKeydown);
        this.drawer.querySelector("[data-drawer-close]")?.focus();
      }

      closeDrawer() {
        if (!this.drawer || this.drawer.hidden) return;
        this.drawer.classList.remove("is-open");
        document.body.classList.remove("drawer-open");
        this.openBtn?.setAttribute("aria-expanded", "false");
        document.removeEventListener("keydown", this.handleKeydown);
        // Hide after the slide-out finishes (matches the CSS transition; also covers
        // reduced-motion, where the transition is instant).
        window.setTimeout(() => {
          this.drawer.hidden = true;
        }, 300);
        this.openBtn?.focus();
      }

      handleKeydown(event) {
        if (event.key === "Escape") {
          this.closeDrawer();
        } else if (event.key === "Tab") {
          this.trapTab(event);
        }
      }

      trapTab(event) {
        const focusables = this.drawer.querySelectorAll(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    },
  );
}